// apps/admin/src/pages/ComplianceReportsPage.tsx
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { FileText, Download, Calendar, TrendingUp, Shield, AlertTriangle, Database, Eye, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

type ReportType = 'quarterly' | 'annual' | 'company_access' | 'data_exports' | 'suspicious_activity';

interface ReportConfig {
  reportType: ReportType;
  startDate?: string;
  endDate?: string;
  companyId?: string;
  adminUid?: string;
}

const ComplianceReportsPage: React.FC = () => {
  const [generating, setGenerating] = useState(false);
  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    reportType: 'quarterly',
  });
  const [generatedReport, setGeneratedReport] = useState<any>(null);

  const reportTypes: Array<{ value: ReportType; label: string; description: string; icon: any }> = [
    {
      value: 'quarterly',
      label: 'Quarterly Access Report',
      description: 'Admin activity summary for the last quarter',
      icon: Calendar,
    },
    {
      value: 'annual',
      label: 'Annual Compliance Report',
      description: 'Full year compliance report for audits',
      icon: FileText,
    },
    {
      value: 'company_access',
      label: 'Company Access Report',
      description: 'Who accessed which companies and when',
      icon: Database,
    },
    {
      value: 'data_exports',
      label: 'Data Exports Report',
      description: 'Summary of all data exports by company',
      icon: Download,
    },
    {
      value: 'suspicious_activity',
      label: 'Suspicious Activity Report',
      description: 'Security alerts and suspicious patterns',
      icon: AlertTriangle,
    },
  ];

  const generateReport = async () => {
    try {
      setGenerating(true);
      setGeneratedReport(null);

      const generateComplianceReport = httpsCallable(functions, 'generateComplianceReport');
      const result = await generateComplianceReport(reportConfig);

      setGeneratedReport(result.data);
      toast.success('Report generated successfully');
    } catch (err: any) {
      console.error('Error generating report:', err);
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = () => {
    if (!generatedReport) return;

    const dataStr = JSON.stringify(generatedReport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${generatedReport.reportId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    if (!generatedReport) return;

    let csvContent = '';

    // Generate CSV based on report type
    if (reportConfig.reportType === 'quarterly' || reportConfig.reportType === 'annual') {
      csvContent = generateAccessReportCSV(generatedReport);
    } else if (reportConfig.reportType === 'company_access') {
      csvContent = generateCompanyAccessCSV(generatedReport);
    } else if (reportConfig.reportType === 'data_exports') {
      csvContent = generateDataExportsCSV(generatedReport);
    } else if (reportConfig.reportType === 'suspicious_activity') {
      csvContent = generateSuspiciousActivityCSV(generatedReport);
    }

    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${generatedReport.reportId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6 text-white">
      {/* Gradient Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-teal-600 to-cyan-600 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Compliance Reports</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Generate compliance reports for audits and regulatory requirements.
            Privacy Act APP 13, ASIC RG255, ISO 27001 compliance.
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm">Privacy Act APP 13</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">ASIC RG255</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="text-sm">ISO 27001</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8">
        {/* Report Configuration */}
        <div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Report Configuration</h2>

            {/* Report Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">Report Type</label>
              <div className="space-y-2">
                {reportTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setReportConfig({ ...reportConfig, reportType: type.value })}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        reportConfig.reportType === type.value
                          ? 'bg-blue-900/30 border-blue-500'
                          : 'bg-gray-900 border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className={`w-5 h-5 mt-1 ${reportConfig.reportType === type.value ? 'text-blue-400' : 'text-gray-400'}`} />
                        <div>
                          <div className="font-semibold text-white">{type.label}</div>
                          <div className="text-sm text-gray-400">{type.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date Range (Optional) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Custom Date Range (Optional)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={reportConfig.startDate || ''}
                    onChange={(e) => setReportConfig({ ...reportConfig, startDate: e.target.value })}
                    style={{ colorScheme: 'dark' }}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={reportConfig.endDate || ''}
                    onChange={(e) => setReportConfig({ ...reportConfig, endDate: e.target.value })}
                    style={{ colorScheme: 'dark' }}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Company ID (Optional) */}
            {(reportConfig.reportType === 'company_access' || reportConfig.reportType === 'data_exports') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company ID (Optional)
                </label>
                <input
                  type="text"
                  value={reportConfig.companyId || ''}
                  onChange={(e) => setReportConfig({ ...reportConfig, companyId: e.target.value })}
                  placeholder="Filter by specific company"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            )}

            <button
              onClick={generateReport}
              disabled={generating}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Report Preview */}
        <div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Report Preview</h2>
              {generatedReport && (
                <div className="flex gap-2">
                  <button
                    onClick={downloadCSV}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </button>
                  <button
                    onClick={downloadReport}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    JSON
                  </button>
                </div>
              )}
            </div>

            {!generatedReport ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <p>No report generated yet.</p>
                <p className="text-sm mt-2">Configure and generate a report to see the preview.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Report Summary */}
                <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Summary</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(generatedReport.summary).map(([key, value]) => (
                      <div key={key}>
                        <div className="text-xs text-gray-400">{formatKey(key)}</div>
                        <div className="text-lg font-semibold text-white">{formatValue(value)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Report Details */}
                <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Details</h3>
                  <pre className="text-xs text-gray-300 bg-gray-800 p-4 rounded overflow-x-auto max-h-96 font-mono">
                    {JSON.stringify(generatedReport.details, null, 2)}
                  </pre>
                </div>

                {/* Report Metadata */}
                <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Report Information</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-gray-400">Report ID:</span>{' '}
                      <span className="text-gray-300 font-mono">{generatedReport.reportId}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Generated At:</span>{' '}
                      <span className="text-gray-300">{new Date(generatedReport.generatedAt).toLocaleString('en-AU')}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Generated By:</span>{' '}
                      <span className="text-gray-300">{generatedReport.generatedBy.name} ({generatedReport.generatedBy.email})</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Period:</span>{' '}
                      <span className="text-gray-300">
                        {new Date(generatedReport.period.startDate).toLocaleDateString('en-AU')} -{' '}
                        {new Date(generatedReport.period.endDate).toLocaleDateString('en-AU')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatValue(value: any): string {
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
}

function generateAccessReportCSV(report: any): string {
  const rows = [
    ['Admin UID', 'Admin Email', 'Admin Name', 'Total Operations', 'Companies Accessed', 'Company Views', 'Data Exports', 'Sensitive Operations'],
  ];

  report.details.adminActivity.forEach((admin: any) => {
    rows.push([
      admin.adminUid,
      admin.adminEmail,
      admin.adminName,
      admin.totalOperations,
      admin.uniqueCompaniesAccessed,
      admin.companyViews,
      admin.dataExports,
      admin.sensitiveOperations,
    ]);
  });

  return rows.map(row => row.join(',')).join('\n');
}

function generateCompanyAccessCSV(report: any): string {
  const rows = [
    ['Company ID', 'Company Name', 'Total Views', 'Unique Admins', 'Admin Email', 'View Count', 'Last Access'],
  ];

  report.details.companyAccess.forEach((company: any) => {
    company.adminAccess.forEach((admin: any) => {
      rows.push([
        company.companyId,
        company.companyName,
        company.totalViews,
        company.uniqueAdmins,
        admin.adminEmail,
        admin.viewCount,
        admin.lastAccess || 'N/A',
      ]);
    });
  });

  return rows.map(row => row.join(',')).join('\n');
}

function generateDataExportsCSV(report: any): string {
  const rows = [
    ['Company ID', 'Company Name', 'Export Count', 'Total Records', 'Total Size (MB)', 'Admin Email', 'Format', 'Record Count', 'Size (MB)', 'Timestamp'],
  ];

  report.details.companyExports.forEach((company: any) => {
    company.exports.forEach((exp: any) => {
      rows.push([
        company.companyId,
        company.companyName,
        company.exportCount,
        company.totalRecords,
        company.totalSizeMB.toFixed(2),
        exp.adminEmail,
        exp.format,
        exp.recordCount,
        exp.sizeMB?.toFixed(2) || '0',
        exp.timestamp || 'N/A',
      ]);
    });
  });

  return rows.map(row => row.join(',')).join('\n');
}

function generateSuspiciousActivityCSV(report: any): string {
  const rows = [
    ['Type', 'Severity', 'Admin Email', 'Description', 'Detected At', 'Acknowledged'],
  ];

  report.details.recentAlerts.forEach((alert: any) => {
    rows.push([
      alert.type,
      alert.severity,
      alert.adminEmail,
      `"${alert.description}"`,
      alert.detectedAt?.toDate?.()?.toISOString() || new Date(alert.detectedAt).toISOString(),
      alert.acknowledged ? 'Yes' : 'No',
    ]);
  });

  return rows.map(row => row.join(',')).join('\n');
}

export default ComplianceReportsPage;
